import { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Card,
    Typography,
    Paper,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    Checkbox,
    InputLabel,
    Snackbar,
    Alert,
    AlertColor,
    Stack
} from '@mui/material';
import { ButtonView } from '../../components/Button';
import { ConfigTableDialog } from './InnerComponent';
import { DeletePrompt } from './InnerComponent/DeletePrompt';
import { Input, Switches } from '../../components';
import { PriceTable } from '../../components/PriceTable';
import {
    getResultSummary,
    updatePriceTableName,
    deleteResultTable,
    addQuoteTestToResultTable,
    getPriceTableName,
    getMatrixType
} from 'services';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MiscellaneousServicesOutlinedIcon from '@mui/icons-material/MiscellaneousServicesOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useSearchParams } from 'react-router';

interface Row {
    id: string;
    tableName: string;
    sampleType: string;
    total: number;
}

function subtotal(items: readonly Row[]) {
    return items.map(({ total }) => total).reduce((sum, i) => sum + i, 0);
}

const columns = [
    'Analysis Name',
    'Total Price'
];

export const ResultPage = ({
    quoteID,
    getPriceTableResult,
    resultData,
    resultTableNames,
    setSelectionTriggerMisc,
    saveAllTrigger,
    summaryUpdateStatus,
    showSummaryTable
}: any) => {
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [tableItems, setTableItems] = useState([] as any[]);
    const [enableSummary, setEnableSummary] = useState(false);
    const [summaryResult, setSummaryResult] = useState([] as any[]);
    const [resultLoadData, setResultLoadData] = useState([] as any[]);
    const [getTableId, setGetTableId] = useState('');
    const [deletePopup, setDeletePopup] = useState(false);
    const [isNameUpdate, setIsNameUpdate] = useState(true);
    const [miscTestPopup, setMiscTestPopup] = useState(false);
    const [formData, setFormData] = useState(
        {
            "quoteResultTableID": "",
            "testID": -1,
            "testName": "",
            "matrixType": "",
            "matrixTypeID": "",
            "isSubContracted": false,
            "testPrice": "",
            "isSet": false,
            "noOfSamples": "",
            "isMiscTest": true,
            "noExtraCharges": false
        }
    );
    const [notificationMsg, setNotificationMsg] = useState<{
        type: AlertColor | '',
        message: string,
        open: boolean
    }>({
        type: '',
        message: '',
        open: false
    });
    const [priceTableNameList, setPriceTableNameList] = useState([] as any[]);
    const [miscTestTrigger, setMiscTestTrigger] = useState(0);
    const [saveAllTriggers, setSaveAllTriggers] = useState<Record<string, number>>({});
    const [isSilentSaveAll, setIsSilentSaveAll] = useState(false);
    const [matrixType, setMatrixType] = useState([] as any[]);

    const [searchparams] = useSearchParams();
    let isEdit = searchparams.get('status') !== null;

    const isSummaryEnabled = (value: any) => value === true || value === 'true' || value === 1;

    useEffect(() => {
        if (resultData) {
            setResultLoadData(resultData);
        }
    }, [resultData])

    useEffect(() => {
        getMatrixType().then((res: any) => {
            setMatrixType(res.data);
        });
    }, []);

    // Reflect the saved Summary Table toggle when a quote is loaded for edit.
    useEffect(() => {
        if (isEdit) {
            setEnableSummary(isSummaryEnabled(showSummaryTable));
        }
    }, [showSummaryTable, isEdit])

    useEffect(() => {
        if (resultTableNames) {
            setTableItems(resultTableNames);
        }
    }, [resultTableNames])

    // Parent (Save Draft) requested a Save All across every result table.
    useEffect(() => {
        if (!saveAllTrigger || tableItems.length === 0) {
            return;
        }

        setIsSilentSaveAll(true);
        setSaveAllTriggers(prev => {
            const next = { ...prev };
            tableItems.forEach((table: any) => {
                next[table.id] = (next[table.id] || 0) + 1;
            });
            return next;
        });
    }, [saveAllTrigger])

    const handleConfTable = (e: any) => {
        setConfigDialogOpen(true);
    }

    const getTableDetails = (items: any) => {
        setTableItems(items);
        initialTableNameUpdate(items);
    }

    const initialTableNameUpdate = (val: any) => {
        val.map((item: any) => {
            if (item?.id) {
                tableNameUpdate(item?.name, item?.id, item?.reference || '', item?.sampleNumberReference || '');
            }
        })
    }

    const handleResultTable = (id: any) => {
        setDeletePopup(true);
        setGetTableId(id);
    }

    const handleTableDelete = (id: any) => {
        deleteResultTable(getTableId).then((res: any) => {
            if (res.status === 200) {
                setDeletePopup(false);
                let newArr = removeTableWithId(tableItems, getTableId);
                setTableItems(newArr);
                setNotificationMsg({
                    type: 'success',
                    message: 'Result table deleted successfully.',
                    open: true
                });
            } else {
                setNotificationMsg({
                    type: 'error',
                    message: res?.data || 'Failed to delete result table. Please try again.',
                    open: true
                });
            }
        }).catch((err: any) => {
            setNotificationMsg({
                type: 'error',
                message: err?.response?.data || 'Failed to delete result table. Please try again.',
                open: true
            });
        });
    }

    const removeTableWithId = (arr: any, sId: any) => {
        return arr.filter((obj: any) => obj.id !== sId);
    }

    const handleInputChange = (e: any, id: any, index: any, field: 'name' | 'reference' | 'sampleNumberReference') => {
        const updatedItems = [...tableItems];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: e.target.value
        };
        setTableItems(updatedItems);
    }

    const handleInputBlur = (index: any) => {
        const selectedItem = tableItems[index];

        tableNameUpdate(
            selectedItem?.name,
            selectedItem?.id,
            selectedItem?.reference || '',
            selectedItem?.sampleNumberReference || ''
        );
    }

    const tableNameUpdate = (item: any, id: any, reference: any = '', sampleNumberReference: any = '') => {
        if (item !== '') {
            let obj = {
                'id': id,
                'name': item,
                'reference': reference,
                'sampleNumberReference': sampleNumberReference
            }
            updatePriceTableName(obj).then((res: any) => {
                if (res.status === 200) {
                    getPriceTableResult(true);
                }
            });
            setIsNameUpdate(false);
            return;
        }

        setIsNameUpdate(true);
    }

    useEffect(() => {
        if (enableSummary) {
            resultSummary(quoteID);
        }
    }, [enableSummary])

    const resultSummary = (quoteID: any) => {
        getResultSummary(quoteID).then((res: any) => {
            if (res.status === 200) {
                getPriceTableResult(true);
                setSummaryResult(res.data);
            }
        });
    }

    const gridReload = (status: any) => {
        resultSummary(quoteID);
    }

    const handleSummary = (ev: any) => {
        const checked = ev.target.checked;
        setEnableSummary(checked);
        summaryUpdateStatus?.(checked);
    }

    const getMatrixTypeName = (id: any) => matrixType.find(item => item.id === id)?.name;

    const handleAddTestToTable = () => {
        let list: any = [];
        let obj = {
            "quoteResultTableID": formData.quoteResultTableID,
            "quoteID": quoteID,
            "testID": -1,
            "testName": formData?.testName,
            "matrixType": getMatrixTypeName(formData.matrixTypeID),
            "matrixTypeID": formData.matrixTypeID,
            "userMatrixType": getMatrixTypeName(formData.matrixTypeID),
            "userMatrixTypeID": formData.matrixTypeID,
            "isSubContracted": false,
            "testPrice": formData?.testPrice,
            "isSet": false,
            "noOfSamples": formData?.noOfSamples,
            "isMiscTest": true,
            "noExtraCharges": formData?.noExtraCharges
        }
        list.push(obj);
        addQuoteTestToResultTable(list).then((res: any) => {
            if (res?.status === 200) {
                setSelectionTriggerMisc((prev: any) => prev + 1);
                setNotificationMsg({
                    type: 'success',
                    message: 'Test added to the table successfully.',
                    open: true
                });
                setFormData({
                    "quoteResultTableID": "",
                    "testID": -1,
                    "testName": "",
                    "matrixType": "",
                    "matrixTypeID": "",
                    "isSubContracted": false,
                    "testPrice": "",
                    "isSet": false,
                    "noOfSamples": "",
                    "isMiscTest": true,
                    "noExtraCharges": false
                });
            } else {
                setNotificationMsg({
                    type: 'error',
                    message: res?.data || 'Failed to add test to the table. Please try again.',
                    open: true
                });
            }
        }).catch((err: any) => {
            setNotificationMsg({
                type: 'error',
                message: err?.response?.data || 'Failed to add test to the table. Please try again.',
                open: true
            });
        });
    }

    useEffect(() => {
        priceTableName();
    }, [quoteID, miscTestTrigger])

    const priceTableName = () => {
        if (quoteID) {
            getPriceTableName(quoteID).then((res: any) => {
                if (res.status === 200) {
                    setPriceTableNameList(res.data);
                }
            });
        }
    }

    const handleMiscTestPopupOpen = () => {
        setMiscTestPopup(true);
        setMiscTestTrigger((prev) => prev + 1);
    }

    // Check if all required fields are filled
    const isFormValid = formData.quoteResultTableID &&
        formData.testName &&
        formData.testPrice &&
        formData.noOfSamples;

    const tableMapping = () => {
        return (
            tableItems && tableItems.map((el: any, index: any) => {
                return (
                    <Box key={el.id}>
                        <DeletePrompt
                            id={el.id}
                            billingGroupEnable={false}
                            open={deletePopup}
                            onClose={() => setDeletePopup(false)}
                            handleTableDelete={() => handleTableDelete(el.id)}
                        />
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                <Input
                                    placeholder={el.name}
                                    value={el.name}
                                    variant="filled"
                                    id="outlined-controlled"
                                    size="small"
                                    hiddenLabel
                                    sx={{
                                        flex: 1,
                                        '&:before': {
                                            borderBottom: 'none'
                                        },
                                        '.MuiFilledInput-root .MuiInputBase-input': {
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }
                                    }}
                                    onChange={(e: any) => handleInputChange(e, el.id, index, 'name')}
                                    onBlur={() => handleInputBlur(index)}
                                    required
                                    helperText={isNameUpdate ? "Please update the table name." : ''}
                                />
                                <Input
                                    placeholder="Suite Code Reference"
                                    value={el.reference || ''}
                                    variant="filled"
                                    id={`reference-${el.id}`}
                                    size="small"
                                    hiddenLabel
                                    sx={{
                                        minWidth: '180px',
                                        '&:before': {
                                            borderBottom: 'none'
                                        },
                                        '.MuiFilledInput-root .MuiInputBase-input': {
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }
                                    }}
                                    onChange={(e: any) => handleInputChange(e, el.id, index, 'reference')}
                                    onBlur={() => handleInputBlur(index)}
                                />
                                <Input
                                    placeholder="Sample Number Reference"
                                    value={el.sampleNumberReference || ''}
                                    variant="filled"
                                    id={`sample-number-reference-${el.id}`}
                                    size="small"
                                    hiddenLabel
                                    sx={{
                                        minWidth: '220px',
                                        '&:before': {
                                            borderBottom: 'none'
                                        },
                                        '.MuiFilledInput-root .MuiInputBase-input': {
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }
                                    }}
                                    onChange={(e: any) => handleInputChange(e, el.id, index, 'sampleNumberReference')}
                                    onBlur={() => handleInputBlur(index)}
                                />
                                <DeleteOutlineOutlinedIcon
                                    color='error'
                                    sx={{ fontSize: 24, cursor: 'pointer', flexShrink: 0 }}
                                    onClick={() => handleResultTable(el.id)}
                                />
                            </div>
                            <ButtonView
                                name="Save All"
                                variant="contained"
                                color="primary"
                                startIcon={<SaveOutlinedIcon />}
                                sx={{ flexShrink: 0 }}
                                onClick={() => {
                                    setIsSilentSaveAll(false);
                                    setSaveAllTriggers(prev => ({ ...prev, [el.id]: (prev[el.id] || 0) + 1 }));
                                }}
                            />
                        </div>
                        {isNameUpdate ?
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '10px', fontStyle: 'italic', margin: '0px 0px 10px 10px' }}
                            >
                                (Please configure the table name.)
                            </Typography>
                            : null}
                        <PriceTable
                            id={el.id}
                            result={resultLoadData || null}
                            status='result'
                            gridReload={gridReload}
                            setSelectionTriggerMisc={setSelectionTriggerMisc}
                            saveAllTrigger={saveAllTriggers[el.id] || 0}
                            saveAllSilent={isSilentSaveAll}
                        />
                        <br />
                    </Box>
                )
            }
            )
        )
    }

    useEffect(() => {
        tableMapping();
    }, [resultLoadData])

    return (
        <div style={{ paddingTop: '30px' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    '& > :not(style)': {
                        m: 0,
                        p: 0,
                        width: "100%",
                        height: "auto",
                        maxWidth: "100%"
                    }
                }}
            >
                <Container maxWidth="xl" sx={{ marginBottom: 5, marginTop: 5, padding: 0 }}>
                    <Card variant="outlined" sx={{ padding: 4 }}>
                        <Typography variant="h6" gutterBottom mt={2} mb={2}>
                            Results Table
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', marginBottom: '16px' }}>
                            <ButtonView
                                name="Configure Result Tables"
                                variant="contained"
                                color="primary"
                                onClick={handleConfTable}
                                startIcon={<SettingsOutlinedIcon />}
                            />
                            <ButtonView
                                name="Misc Test"
                                variant="contained"
                                color="primary"
                                onClick={handleMiscTestPopupOpen}
                                startIcon={<MiscellaneousServicesOutlinedIcon />}
                                sx={{ minWidth: '120px', whiteSpace: 'nowrap' }}
                            />
                        </div>
                        <ConfigTableDialog
                            open={configDialogOpen}
                            onClose={() => {
                                setConfigDialogOpen(false);
                            }}
                            getTableDetails={getTableDetails}
                            quoteID={quoteID}
                        />

                        {/* Misc Test Popup */}
                        <Dialog
                            open={miscTestPopup}
                            onClose={() => {
                                setMiscTestPopup(false);
                                // Reset form data when dialog closes
                                setFormData({
                                    "quoteResultTableID": "",
                                    "testID": -1,
                                    "testName": "",
                                    "matrixType": "",
                                    "matrixTypeID": "",
                                    "isSubContracted": false,
                                    "testPrice": "",
                                    "isSet": false,
                                    "noOfSamples": "",
                                    "isMiscTest": true,
                                    "noExtraCharges": false
                                });
                            }}
                            maxWidth="md"
                            fullWidth
                        >
                            <DialogTitle>Misc Test</DialogTitle>
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                handleAddTestToTable();
                                setMiscTestPopup(false);
                            }}>
                                <DialogContent sx={{ pt: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="quote-result-table-label" sx={{ fontSize: '12px' }}>
                                                    Select Result Table
                                                </InputLabel>
                                                <Select
                                                    labelId="quote-result-table-label"
                                                    id="quote-result-table"
                                                    value={formData.quoteResultTableID || ""}
                                                    label="Select Result Table"
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            quoteResultTableID: e.target.value,
                                                        })
                                                    }
                                                >
                                                    {priceTableNameList?.map((item: any) => (
                                                        <MenuItem key={item.id} value={item.id}>
                                                            {item.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="matrix-type-label" sx={{ fontSize: '12px' }}>
                                                    Select Matrix Type
                                                </InputLabel>
                                                <Select
                                                    labelId="matrix-type-label"
                                                    id="matrix-type"
                                                    value={formData.matrixTypeID || ""}
                                                    label="Select Matrix Type"
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            matrixTypeID: e.target.value,
                                                        })
                                                    }
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    {matrixType?.map((item: any) => (
                                                        <MenuItem key={item.id} value={item.id} sx={{ fontSize: '12px' }}>
                                                            {item.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                size="small"
                                                label="Test Name"
                                                placeholder="Enter test name"
                                                variant="outlined"
                                                required
                                                fullWidth
                                                value={formData.testName}
                                                onChange={(e: any) => setFormData({ ...formData, testName: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                size="small"
                                                label="Test Price"
                                                type="number"
                                                placeholder="Enter test price"
                                                variant="outlined"
                                                required
                                                fullWidth
                                                value={formData.testPrice}
                                                onChange={(e: any) => setFormData({ ...formData, testPrice: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Sample Number"
                                                placeholder="Enter sample number"
                                                variant="outlined"
                                                required
                                                fullWidth
                                                value={formData.noOfSamples}
                                                onChange={(e: any) => setFormData({ ...formData, noOfSamples: e.target.value })}
                                            />
                                        </Grid>
                                        {/* <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.noExtraCharges}
                                                        onChange={(e: any) => setFormData({ ...formData, noExtraCharges: e.target.checked })}
                                                        color="primary"
                                                    />
                                                }
                                                label="No Extra Charges"
                                            />
                                        </Grid> */}
                                    </Grid>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        onClick={() => {
                                            setMiscTestPopup(false);
                                            // Reset form data when cancel is clicked
                                            setFormData({
                                                "quoteResultTableID": "",
                                                "testID": -1,
                                                "testName": "",
                                                "matrixType": "",
                                                "matrixTypeID": "",
                                                "isSubContracted": false,
                                                "testPrice": "",
                                                "isSet": false,
                                                "noOfSamples": "",
                                                "isMiscTest": true,
                                                "noExtraCharges": false
                                            });
                                        }}
                                        variant='outlined'
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type='submit'
                                        disabled={!isFormValid}
                                    >
                                        Add Test
                                    </Button>
                                </DialogActions>
                            </form>
                        </Dialog>
                        <div>
                            <Grid container spacing={3}>
                                <Grid item xs={6} sm={6} md={2} style={{ paddingTop: 0 }}>
                                    <Typography variant="subtitle1" gutterBottom mt={2} mb={2}>
                                        Summary Table to be enabled?
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={6} md={2} style={{ paddingTop: '8px' }}>
                                    <Switches onChange={handleSummary} checked={enableSummary} />
                                </Grid>
                            </Grid>
                            {enableSummary ?
                                <TableContainer component={Paper}>
                                    <Table sx={{ minWidth: 700, border: '1px solid #ddd', paddingTop: 10 }} aria-label="spanning table">
                                        <TableHead style={{ backgroundColor: '#003883' }}>
                                            <TableRow>
                                                {columns.map((column) => (
                                                    <TableCell style={{ color: '#fff' }}>{column}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {summaryResult.map((row: any) => (
                                                <TableRow key={row.id}>
                                                    <TableCell>{row.tableName}</TableCell>
                                                    <TableCell>{row.total}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow style={{ backgroundColor: '#0000000a' }}>
                                                <TableCell colSpan={1}><b>Total</b></TableCell>
                                                <TableCell><b>{subtotal(summaryResult).toFixed(2)}</b></TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                : null}
                            <br />
                        </div>
                        {tableItems ? tableMapping() : null}
                    </Card>
                </Container>
            </Box>
            <Stack spacing={2} sx={{ width: '100%' }}>
                <Snackbar
                    open={notificationMsg.open}
                    autoHideDuration={6000}
                    onClose={() => setNotificationMsg({ ...notificationMsg, open: false })}
                >
                    <Alert onClose={() => setNotificationMsg({ ...notificationMsg, open: false })} severity={notificationMsg?.type || 'info'} sx={{ width: '100%' }}>
                        {notificationMsg?.message}
                    </Alert>
                </Snackbar>
            </Stack>
        </div >
    );
}
